import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PendingDecision, SectionTitle } from '@/components/editorial';
import { Button } from '@/components/ui/button';
import { api } from '@/services/api';
import {
  claimMatches,
  formatUsd,
  groupClaimsByMonth,
  isScanning,
  ledgerGroupLabel,
  type CategoryScope,
  type ClaimScope,
  receiptSlice,
  type ReceiptSlice,
} from '@/lib/bills-display';
import type { ReimbursementClaim } from '@/types/api';
import { ExpenseRow } from './ExpenseRow';

/**
 * The Expenses & receipts ledger (Finances collector spec 2026-09-10 §4.3):
 * "Needs your attention" first, then one group per month with the month's
 * total at the right of its heading, newest first. Rows are `ExpenseRow`.
 *
 * Reads the family's claims through the shared `['reimbursements-mine']`
 * query — the same key GrantsPanel's receipt picker uses, so the two can
 * never disagree. While any row is still scanning, the list polls every 3 s;
 * `isScanning` stops after a minute per row, so polling stops with it.
 */
export const CLAIMS_QUERY_KEY = ['reimbursements-mine'] as const;

export interface ExpenseLedgerProps {
  search: string;
  claimScope: ClaimScope;
  categoryScope: CategoryScope;
  onOpen: (claim: ReimbursementClaim) => void;
  onEdit: (claim: ReimbursementClaim) => void;
  onDelete: (claim: ReimbursementClaim) => void;
  /** The empty state's one inline call to action. */
  onAddReceipt: () => void;
  /**
   * Which slice of the family's receipts to render.
   *
   *   `todo`             every receipt still the family's to act on, linked to
   *                      a grant or not (2026-10-03). It used to be unlinked
   *                      only, under "Needs a grant" — but none of the four
   *                      attention states is about a grant, and a linked
   *                      receipt needing an answer was visible only inside its
   *                      expanded grant.
   *   `unlinked-with-trf` unlinked, sent in and not yet decided (#2658):
   *                      "With TRF" on /bills.
   *   `unlinked-settled` unlinked and decided by TRF — approved, paying,
   *                      paid back, not approved. History, collapsed at the
   *                      foot of /bills. (A linked settled receipt is read
   *                      inside its grant, `GrantReceipts`.) Until #2658 it
   *                      also held receipts still under review.
   *   `all` (default)    every claim, exactly as this component has always
   *                      behaved. Keeps every other caller and its suite
   *                      unchanged.
   */
  scope?: 'all' | 'todo' | 'unlinked-with-trf' | 'unlinked-settled';
  /**
   * The rung its group headings sit on. Default `h2`, as it has always been.
   * The scoped callers pass `h3`: on the rebuilt /bills the ledger sits
   * UNDER a page section ("Finish these receipts"), and two h2s in a row would say
   * the outer heading governs nothing.
   */
  headingAs?: 'h2' | 'h3';
  /**
   * Draw the two `(?)` pending-decision markers on the first group heading.
   *
   * Default true, which is what every single-ledger caller has always got.
   * `/bills` renders the ledger TWICE (the to-do and the history), and a
   * marker is a question asked of the reader once — two copies of
   * `expenses-ledger-shape` on one page is both a duplicated question and a
   * duplicated DOM id. The second instance passes false.
   */
  showMarkers?: boolean;
  /**
   * Draw each group's heading. Default true, which is every caller that
   * groups by month.
   *
   * `/bills` passes false for its `todo` instance (#2097). That scope
   * filters to exactly the claims `needsAttention()` returns, so it produces
   * exactly ONE group, always, and that group's heading is always the same
   * string — "Needs your attention". A rung that never branches is not
   * structure, and it lands directly under the page's own "Finish these
   * receipts" heading, so it would say the same thing twice.
   *
   * The group's month TOTAL is unaffected and still prints when it has one;
   * the attention group has none, which is why suppressing its heading
   * leaves nothing behind.
   */
  showGroupHeadings?: boolean;
}

const POLL_MS = 3_000;

/** Each /bills scope is one `receiptSlice` (#2658). */
const SCOPE_SLICE: Record<Exclude<NonNullable<ExpenseLedgerProps['scope']>, 'all'>, ReceiptSlice> = {
  todo: 'todo',
  'unlinked-with-trf': 'with_trf',
  'unlinked-settled': 'past',
};

export function ExpenseLedger({ search, claimScope, categoryScope, onOpen, onEdit, onDelete, onAddReceipt, scope = 'all', headingAs = 'h2', showMarkers = true, showGroupHeadings = true }: ExpenseLedgerProps) {
  const { t } = useTranslation(['money', 'common']);
  // `now` ticks with the poll so a "Scanning…" row can age into "Needs details"
  // without a refetch changing anything.
  const [now, setNow] = useState(() => new Date());

  const claimsQuery = useQuery({
    queryKey: CLAIMS_QUERY_KEY,
    queryFn: () => api.reimbursements.listMine(),
    refetchInterval: (q) => {
      const rows = (q.state.data?.data ?? []) as ReimbursementClaim[];
      return rows.some((c) => isScanning(c, new Date())) ? POLL_MS : false;
    },
    // A parent drops a receipt and switches tabs, or the page sits in a
    // background window: TanStack pauses interval refetches for a hidden
    // document by default, and on the 2026-09-10 production walk the row sat
    // on "Scanning…" for a minute although the scan had answered in four
    // seconds. Keep polling while hidden — it stops on its own once no row is
    // scanning.
    refetchIntervalInBackground: true,
  });
  const claims = useMemo<ReimbursementClaim[]>(() => {
    const rows = (claimsQuery.data?.data ?? []) as ReimbursementClaim[];
    if (scope === 'all') return rows;
    // A SNAPSHOT timestamp, deliberately not the ticking `now` in state.
    // `now` is in the deps of nothing here on purpose: the effect below sets
    // `now` whenever `claims` changes identity, so a memo that both READS
    // `now` and produces a fresh array closes a render loop — `new Date()`
    // is never Object.is-equal, so React never bails out and the page hangs.
    // That is a real hang, not a slow test: it took down BillsPage.chrome
    // before this comment existed. The split only needs a timestamp, never
    // the ticking one — the 60s scanning window it feeds is about whether a
    // row is still scanning, and a row that ages out of `todo` is picked up
    // by the next data change.
    const at = new Date();
    const slice = SCOPE_SLICE[scope];
    return rows.filter((c) => receiptSlice(c, at) === slice);
  }, [claimsQuery.data, scope]);

  const anyScanning = claims.some((c) => isScanning(c, now));
  const refetch = claimsQuery.refetch;
  useEffect(() => {
    if (!anyScanning) return;
    // Belt and braces with refetchIntervalInBackground: the same tick that
    // ages a row also asks the server again, so a stale "Scanning…" cannot
    // outlive the scan by more than one poll.
    const t = setInterval(() => { setNow(new Date()); void refetch(); }, POLL_MS);
    return () => clearInterval(t);
  }, [anyScanning, refetch]);
  useEffect(() => { setNow(new Date()); }, [claims]);

  const visible = useMemo(
    () => claims.filter((c) => claimMatches(c, claimScope, categoryScope, search, now, t)),
    [claims, claimScope, categoryScope, search, now, t],
  );
  const groups = useMemo(() => groupClaimsByMonth(visible, now), [visible, now]);

  if (claimsQuery.isError) {
    return (
      <p className="text-body text-stevens-ink-3" role="status">
        {t('ledger.loadFailed')}{' '}
        <button type="button" className="text-stevens-indigo hover:underline" onClick={() => claimsQuery.refetch()}>{t('common:actions.retry')}</button>
      </p>
    );
  }
  if (claimsQuery.isPending) {
    return <p className="text-body text-stevens-ink-3" role="status">{t('ledger.loading')}</p>;
  }

  // Nothing in this slice. That is the finished state, not an empty one —
  // a "no expenses yet" panel here would be a complaint about a job the
  // family has done. The page renders no heading either (see BillsPage).
  if (scope !== 'all' && claims.length === 0) return null;

  if (claims.length === 0) {
    return (
      <div className="rounded-lg border border-border/60 p-8 text-center" data-testid="ledger-empty">
        <SectionTitle level="card-title" className="mb-1">{t('ledger.emptyTitle')}</SectionTitle>
        <p className="mx-auto mb-4 max-w-prose text-body text-stevens-ink-3">
          {t('ledger.emptyBody')}
        </p>
        <Button type="button" variant="brand" onClick={onAddReceipt}>{t('ledger.emptyCta')}</Button>
      </div>
    );
  }
  if (visible.length === 0) {
    if (scope !== 'all') return null;
    return <p className="text-body text-stevens-ink-3">{t('ledger.noMatch')}</p>;
  }

  return (
    <div className="space-y-6" id="expense-ledger" data-testid="expense-ledger">
      {groups.map((g, gi) => {
        const headingId = `expense-group-${g.key}`;
        const hasTotal = g.total != null;
        const label = ledgerGroupLabel(g, t);
        return (
          // Without a visible heading the group still needs an accessible
          // name, or it becomes an unnamed region a screen reader cannot
          // announce or skip. The label is the group's own, so nothing is
          // lost to assistive tech — only the duplicated ink is.
          <section
            key={g.key}
            {...(showGroupHeadings ? { 'aria-labelledby': headingId } : { 'aria-label': label })}
            className="space-y-2"
          >
            {(showGroupHeadings || hasTotal) && (
              <div className="flex items-baseline justify-between gap-3">
                {showGroupHeadings ? (
                  <div className="flex items-center gap-1.5">
                    <SectionTitle as={headingAs} level="card-title" id={headingId}>{label}</SectionTitle>
                    {gi === 0 && showMarkers && <PendingDecision id="expenses-ledger-shape" side="right" />}
                    {gi === 0 && showMarkers && <PendingDecision id="finances-upcoming-planner" side="right" />}
                  </div>
                ) : (
                  // Keeps `justify-between` pushing a heading-less group's
                  // total to the right edge, where every other total sits.
                  <span />
                )}
                {hasTotal && (
                  <span className="font-mono tabular-nums text-caption text-stevens-ink-3" data-testid="group-total">
                    {formatUsd(g.total)}
                  </span>
                )}
              </div>
            )}
            <ul className="divide-y divide-border/60">
              {g.items.map((c) => (
                <ExpenseRow key={c._id} claim={c} now={now} onOpen={onOpen} onEdit={onEdit} onDelete={onDelete} />
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
